import {
  PlanDataWhenToLeaveWithSelections,
  PlanDataWhenToLeaveWithSelectionsSelectionsEnum,
} from 'api/client';
import Checkbox from 'components/checkbox';
import TextInput from 'components/text-input';
import { Text } from 'components/ui/text';
import WarningSystemsCard from 'components/warning-systems-card';
import React, { FC, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useUnmount } from 'react-use';
import { NavigatePlan } from 'types/base';
import { checklistDataWhenToLeave } from 'utils/config';
import { navigateWithDirection } from 'utils/helpers';
import FormControls from '../form-controls';
import {
  CardWrapper,
  CheckboxFieldWrapper,
  Description,
  QuestionWrapper,
  TextInputWrapper,
  Title,
} from '../question.styles';

interface WhenToLeaveFormData {
  /**
   *
   * @type {Array<string>}
   * @memberof PlanDataWhenToLeaveWithSelections
   */
  selections: Array<PlanDataWhenToLeaveWithSelectionsSelectionsEnum>;
  /**
   *
   * @type string
   * @memberof PlanDataWhenToLeaveWithSelections
   */
  notes?: string;
}
interface Props {
  whenToLeave?: PlanDataWhenToLeaveWithSelections;
  onSetWhenToLeave: (whenToLeave: PlanDataWhenToLeaveWithSelections) => void;
  navigate?: NavigatePlan;
}

const WhenToLeave: FC<Props> = (props) => {
  // This function bridges the gap between the data structure for notes being an object
  // and the form requiring a string to properly set the default value
  const transformedData: WhenToLeaveFormData = useMemo(() => {
    return {
      selections: props.whenToLeave?.selections ?? [],
      notes: props.whenToLeave?.notes?.text[0],
    };
  }, [props.whenToLeave]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<WhenToLeaveFormData>({
    defaultValues: transformedData,
  });

  const onSubmit = handleSubmit(({ selections, notes = '' }, event) => {
    const planDataForSubmission: PlanDataWhenToLeaveWithSelections = {
      selections,
      notes: { text: [notes] },
    };
    props.onSetWhenToLeave(planDataForSubmission);
    navigateWithDirection(props.navigate, event);
  });

  const formData = watch();
  useUnmount(() => {
    if (!isSubmitting && isDirty) {
      const { selections, notes = '' } = formData;
      const planDataWhenToLeaveWithSelections: PlanDataWhenToLeaveWithSelections =
        {
          selections,
          notes: { text: [notes] },
        };

      props.onSetWhenToLeave(planDataWhenToLeaveWithSelections);
    }
  });

  return (
    <form noValidate onSubmit={onSubmit}>
      <QuestionWrapper>
        <Title>When will we leave?</Title>

        <Description>
          <b>
            Do not wait to receive a phone call, text message or knock on the
            door.
          </b>
          <br />
          <br />
          You are responsible for your decision to leave. In a bushfire, it is
          not safe to wait and see. This can make your evacuation more
          dangerous. Know the fire danger ratings and bushfire warning system
          and leave as soon as possible.
        </Description>

        <CardWrapper>
          <WarningSystemsCard />
        </CardWrapper>

        <CheckboxFieldWrapper>
          <Text>
            <b>We will leave when</b>
          </Text>
          <Text>Select as many as required</Text>
          {checklistDataWhenToLeave.map((item, index) => (
            <Checkbox
              id={`${item.enum}`}
              label={item.label}
              name='selections'
              align='center'
              ref={register}
              key={index}
              value={item.enum}
              slim
            />
          ))}
          <TextInputWrapper>
            <TextInput
              className={'paddingTop'}
              label='Notes (optional)'
              placeholder='Type something'
              component='input'
              name='notes'
              id='notes'
              ref={register}
            />
          </TextInputWrapper>
        </CheckboxFieldWrapper>
      </QuestionWrapper>

      <FormControls />
    </form>
  );
};

export default WhenToLeave;
